import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendar, FaEye, FaArrowRight } from 'react-icons/fa';
import { api } from '../App';
import { useLanguage } from '../LanguageContext';
import SEO from '../components/SEO';
import './Blog.css';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const { t, language } = useLanguage();

  const categories = [
    { id: 'all', label: t('blog.allPosts') },
    { id: 'weekly-report', label: t('blog.weeklyReports') },
    { id: 'market-analysis', label: t('blog.marketAnalysis') },
    { id: 'news', label: t('blog.news') },
    { id: 'education', label: t('blog.education') }
  ];

  useEffect(() => {
    fetchPosts();
  }, [activeCategory]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const query = activeCategory === 'all' 
        ? '/api/posts' 
        : `/api/posts?category=${activeCategory}`;
      const data = await api.get(query);
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'sl' ? 'sl-SI' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="blog-page">
      <SEO 
        title={t('blog.seoTitle')}
        description={t('blog.seoDescription')}
        keywords="crypto market analysis, bitcoin weekly report, cryptocurrency news, investment insights, market trends, crypto education"
      />
      
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <motion.div
            className="blog-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-subtitle">{t('blog.ourBlog')}</span>
            <h1 className="blog-title">
              {t('blog.heroTitle')}<br />
              <span className="gold-text">{t('blog.heroTitleGold')}</span>
            </h1>
            <p className="blog-description">
              {t('blog.heroDescription')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="category-filter">
        <div className="container">
          <div className="filter-tabs">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`filter-tab ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="posts-section section">
        <div className="container">
          {loading ? (
            <div className="loading-posts">
              <div className="loading-spinner"></div>
              <p>{t('blog.loadingPosts')}</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="no-posts">
              <h3>{t('blog.noPostsFound')}</h3>
              <p>{t('blog.checkBackSoon')}</p>
            </div>
          ) : (
            <div className="posts-grid">
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  className="post-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  {post.featured_image && (
                    <div className="post-image">
                      <img src={post.featured_image} alt={post.title} />
                    </div>
                  )}
                  <div className="post-content">
                    <div className="post-meta">
                      <span className="post-category">{post.category}</span>
                      <span className="post-date">
                        <FaCalendar /> {formatDate(post.published_at)}
                      </span>
                    </div>
                    <h2 className="post-title">{post.title}</h2>
                    <p className="post-excerpt">{post.excerpt}</p>
                    <div className="post-footer">
                      <Link to={`/blog/${post.slug}`} className="read-more">
                        {t('blog.readMore')} <FaArrowRight />
                      </Link>
                      <span className="post-views">
                        <FaEye /> {post.views || 0}
                      </span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
